//#region header */
/**************************************************************************************************
//
//  Description: Form to display the database connection information
//
//  Copyright:    © 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   05.01.26 Sean Flook         GMSCM-12 Initial Revision.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import React, { useEffect, useState, useRef } from "react";

import useConfigQueryStore from "../context/ConfigStore";
import useUserQueryStore from "../context/UserStore";

import { DatabaseConnection, MultiTenantConfigOrganisation } from "../dtos/multiTenantConfigDto";
import ConfigErrorType from "../models/configErrorType";

import { Box, Grid2 } from "@mui/material";
import ADSTextControl from "../components/ADSTextControl";

import { dataFormStyle, FormRowStyle } from "../utils/ADSStyles";
import ADSNumberControl from "../components/ADSNumberControl";

interface DbConnectionFormProps {
  parentTreeId: string;
  data: DatabaseConnection;
  errors: ConfigErrorType[] | undefined;
  onDataChange: (data: DatabaseConnection) => void;
}

const DbConnectionForm: React.FC<DbConnectionFormProps> = ({ parentTreeId, data, errors, onDataChange }) => {
  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);

  const revertChanges = useConfigQueryStore((s) => s.configQuery.revertChanges);
  const originalDocument = useConfigQueryStore((s) => s.configQuery.originalDocument);
  const organisationSelected = useConfigQueryStore((s) => s.configQuery.organisationSelected);

  const [server, setServer] = useState<string | undefined>();
  const [database, setDatabase] = useState<string | undefined>();
  const [databaseUsername, setDatabaseUsername] = useState<string | undefined>();
  const [databasePassword, setDatabasePassword] = useState<string | undefined>();
  const [databasePort, setDatabasePort] = useState<number | undefined>();

  const [serverError, setServerError] = useState<string[] | undefined>();
  const [databaseError, setDatabaseError] = useState<string[] | undefined>();
  const [usernameError, setUsernameError] = useState<string[] | undefined>();
  const [passwordError, setPasswordError] = useState<string[] | undefined>();
  const [portError, setPortError] = useState<string[] | undefined>();

  const currentServer = useRef<string | undefined>(undefined);
  const currentDatabase = useRef<string | undefined>(undefined);
  const currentUsername = useRef<string | undefined>(undefined);
  const currentPassword = useRef<string | undefined>(undefined);
  const currentPort = useRef<number | undefined>(undefined);

  /**
   * Method to update the data when a field is changed.
   *
   * @param fieldName The name of the field that has been updated.
   * @param value The new value for the field.
   */
  const updateData = (fieldName: string, value: string | number | undefined): void => {
    let updatedDbConnection: DatabaseConnection;
    updatedDbConnection = {
      ...data,
      [fieldName]: value,
    };

    if (onDataChange) {
      onDataChange(updatedDbConnection);
    }
  };

  /**
   * Event to handle when the server is changed.
   *
   * @param newValue The new server.
   */
  const handleServerChangeEvent = (newValue: string): void => {
    setServer(newValue);
    currentServer.current = newValue;
    updateData("server", newValue);
  };

  /**
   * Event to handle when the database is changed.
   *
   * @param newValue The new database.
   */
  const handleDatabaseChangeEvent = (newValue: string): void => {
    setDatabase(newValue);
    currentDatabase.current = newValue;
    updateData("database", newValue);
  };

  /**
   * Event i handle when the username is changed.
   *
   * @param newValue The new username.
   */
  const handleUsernameChangeEvent = (newValue: string): void => {
    setDatabaseUsername(newValue);
    currentUsername.current = newValue;
    updateData("username", newValue);
  };

  /**
   * Event to handle when the password is changed.
   *
   * @param newValue Th new password.
   */
  const handlePasswordChangeEvent = (newValue: string): void => {
    setDatabasePassword(newValue);
    currentPassword.current = newValue;
    updateData("password", newValue);
  };

  /**
   * Event to handle when the port is changed.
   *
   * @param newValue The new port.
   */
  const handlePortChangeEvent = (newValue: number | undefined): void => {
    setDatabasePort(newValue);
    currentPort.current = newValue;
    updateData("port", newValue);
  };

  useEffect(() => {
    if (data) {
      setServer(data.server);
      setDatabase(data.database);
      setDatabaseUsername(data.username);
      setDatabasePassword(data.password);
      setDatabasePort(data.port);
    }
  }, [data]);

  useEffect(() => {
    setServerError(undefined);
    setDatabaseError(undefined);
    setUsernameError(undefined);
    setPasswordError(undefined);
    setPortError(undefined);

    if (errors && errors.length > 0) {
      for (const error of errors) {
        if (error.treeId === parentTreeId) {
          switch (error.field) {
            case "server":
            case "connectionServer":
              setServerError(error.errors);
              break;

            case "database":
            case "connectionDatabase":
              setDatabaseError(error.errors);
              break;

            case "username":
            case "connectionUsername":
              setUsernameError(error.errors);
              break;

            case "password":
            case "connectionPassword":
              setPasswordError(error.errors);
              break;

            case "port":
            case "connectionPort":
              setPortError(error.errors);
              break;

            default:
              break;
          }
        }
      }
    }
  }, [errors, data, parentTreeId]);

  useEffect(() => {
    if (revertChanges && originalDocument) {
      const originalOrganisation: MultiTenantConfigOrganisation = originalDocument.organisations.find(
        (x) => x.organisationName === organisationSelected,
      ) as MultiTenantConfigOrganisation;

      if (originalOrganisation) {
        // setServer(
        //   variant === "search"
        //     ? originalOrganisation.namedConnectionSettings.SearchDatabaseConnection.server
        //     : originalOrganisation.namedConnectionSettings.XDMDatabaseConnection.server
        // );
        // setDatabase(
        //   variant === "search"
        //     ? originalOrganisation.namedConnectionSettings.SearchDatabaseConnection.database
        //     : originalOrganisation.namedConnectionSettings.XDMDatabaseConnection.database
        // );
        // setDatabaseUsername(
        //   variant === "search"
        //     ? originalOrganisation.namedConnectionSettings.SearchDatabaseConnection.username
        //     : originalOrganisation.namedConnectionSettings.XDMDatabaseConnection.username
        // );
        // setDatabasePassword(
        //   variant === "search"
        //     ? originalOrganisation.namedConnectionSettings.SearchDatabaseConnection.password
        //     : originalOrganisation.namedConnectionSettings.XDMDatabaseConnection.password
        // );
        // setDatabasePort(
        //   variant === "search"
        //     ? originalOrganisation.namedConnectionSettings.SearchDatabaseConnection.port
        //     : originalOrganisation.namedConnectionSettings.XDMDatabaseConnection.port
        // );
      }
    }
  }, [revertChanges, originalDocument, organisationSelected]);

  return (
    <Box sx={dataFormStyle("OrganisationForm")}>
      <Grid2 container justifyContent="flex-start" alignItems="baseline" sx={FormRowStyle()}>
        <Grid2 size={12}>
          <ADSTextControl
            label="Server"
            value={server}
            maxLength={100}
            id="server_name"
            isEditable={currentUser?.isSuperAdministrator}
            isRequired
            helperText="The name of the SQL server."
            errorText={serverError}
            onChange={handleServerChangeEvent}
          />
          <ADSTextControl
            label="Database"
            value={database}
            maxLength={100}
            id="database_name"
            isEditable={currentUser?.isSuperAdministrator}
            isRequired
            helperText="The name of the database."
            errorText={databaseError}
            onChange={handleDatabaseChangeEvent}
          />
          <ADSTextControl
            label="User"
            value={databaseUsername}
            maxLength={100}
            id="username"
            isEditable={currentUser?.isSuperAdministrator}
            isRequired
            helperText="The id of the user to use to connect to the database."
            errorText={usernameError}
            onChange={handleUsernameChangeEvent}
          />
          <ADSTextControl
            label="Password"
            value={databasePassword}
            isHidden
            maxLength={100}
            id="user_password"
            isEditable={currentUser?.isSuperAdministrator}
            isRequired
            helperText="The password for the user used to connect to the database."
            errorText={passwordError}
            onChange={handlePasswordChangeEvent}
          />
          <ADSNumberControl
            label="Port"
            value={databasePort}
            isEditable={currentUser?.isSuperAdministrator}
            isRequired
            helperText="The port number to use to connect to the database."
            errorText={portError}
            onChange={handlePortChangeEvent}
          />
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default DbConnectionForm;
